import React, { useEffect, useState } from 'react';

import { styled } from '@mui/material/styles';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import FileUpload from '@fd/ui/FileUpload';
import FormItemLayout from '@fd/ui/Forms/FormItemLayout';

import checkeredBackground from '../../../../assets/images/checkered.svg';
import { formikValidate } from '../../../../helpers/validation';
import FormItem, { RenderProps } from '../../components/FormItem';

const StyledBrandCoverPhotoWrapper = styled('div')(() => ({
  width: 240,
  padding: '15px 15px 15px 15px',
  marginBottom: '15px',
  backgroundImage: `url("${checkeredBackground}")`,
  backgroundSize: 'auto 110%',
  backgroundPositionY: '-15px',
}));

const DEFAULT_BRAND_COVER_PHOTO_URL =
  'https://flipdish.imgix.net/gddSky3brWXB4bHWcfax5M130.jpg?w=240&h=120&fm=jpg&dpr=1&fit=clamp';

type InnerProps = MappedProps;
type OuterProps = {
  brandCoverPhotoUrl: string;
};

type Props = InnerProps & OuterProps;

const validateBrandCoverPhoto = (
  value: File,
  setBrandCoverPhoto: (data: string | null) => void
) => {
  return new Promise<void | JSX.Element | string>((resolve) => {
    if (!value || !(value instanceof File)) {
      return resolve();
    }
    const isNotValidExtension = formikValidate.extension(value, ['png', 'jpeg', 'jpg']);
    if (isNotValidExtension) {
      return resolve(isNotValidExtension);
    }

    const maxAllowedSize = 18 * 1024 * 1024; // 18 MB
    if (value.size > maxAllowedSize) {
      return resolve(<Translate id="Max_file_size_exceeded" data={{ size: '18 MB' }} />);
    }

    const reader = new FileReader();
    reader.readAsDataURL(value);

    reader.onload = (event) => {
      if (typeof event.target?.result === 'string') {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          if (img.width < 1920 || img.height < 1080) {
            return resolve(
              <Translate id="Wrong_image_size" data={{ width: 1920, height: 1080 }} />
            );
          }

          const aspectRatio = img.width / img.height;
          const requiredAspectRatio = 16 / 9;
          const tolerance = 0.5;

          if (Math.abs(aspectRatio - requiredAspectRatio) > tolerance) {
            return resolve(
              <Translate id="Wrong_image_aspect_ratio" data={{ aspectRatio: '16:9' }} />
            );
          }

          setBrandCoverPhoto(img.src);
          return resolve();
        };

        img.onerror = () => {
          return resolve(<Translate id="Error_please_try_again_later" />);
        };
      } else {
        return resolve(<Translate id="Error_please_try_again_later" />);
      }
    };

    reader.onerror = () => {
      resolve(<Translate id="Error_please_try_again_later" />);
    };
  });
};

const BrandCoverPhotoField = ({ brandCoverPhotoUrl, translate }: Props) => {
  const [brandCoverPhoto, setBrandCoverPhoto] = useState<string | null>(brandCoverPhotoUrl);
  useEffect(() => {
    setBrandCoverPhoto((prevCoverPhoto) => {
      if (!prevCoverPhoto || prevCoverPhoto === brandCoverPhotoUrl) {
        return brandCoverPhotoUrl;
      } else {
        return prevCoverPhoto;
      }
    });
  }, [brandCoverPhotoUrl]);

  return (
    <FormItemLayout label={<Translate id="Cover_photo" />}>
      <FormItem
        name="brandCoverPhoto"
        // @ts-ignore
        validate={(file: File) => validateBrandCoverPhoto(file, setBrandCoverPhoto)}
        secondary={translate('Cover_photo_requirements') as TranslationId}
      >
        {(renderProps: RenderProps) => {
          const fieldName = renderProps.field.name;
          const setFieldValue = renderProps.form.setFieldValue;
          return (
            <StyledBrandCoverPhotoWrapper>
              <FileUpload
                fdKey="brandCoverPhoto"
                url={brandCoverPhoto || DEFAULT_BRAND_COVER_PHOTO_URL}
                skipImageTransformation={true}
                onChange={(data?: File) => {
                  setFieldValue(fieldName, data);
                  if (!data) {
                    setBrandCoverPhoto(null);
                  }
                }}
              />
            </StyledBrandCoverPhotoWrapper>
          );
        }}
      </FormItem>
    </FormItemLayout>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => ({
  translate: getTranslate(state.locale),
});

export default compose<InnerProps, OuterProps>(connect(mapStateToProps))(BrandCoverPhotoField);
